import {Tab, Tabs, useMediaQuery, useTheme} from '@mui/material';
import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useLocation, useNavigate} from 'react-router';
import {TabContext, TabPanel} from "@mui/lab";
import CompanyProfile from "../../components/companyProfile/CompanyProfile";
import UserManagement from "../../components/user/manageUsers/UserManagement";
import PaymentMethods from "../../components/paymentMethods/PaymentMethods";
import SubscriptionManager from "../../components/subscriptions/SubscriptionManager";
import {hasAdminAccess, isBuyerAccount, isSupplierAccount} from "../../util/AccountUtils";
import {SecurSpaceContainer, SecurSpaceReportContainer} from "../../components/common";
import {TabContextContainer} from "../../components/common/reports/tabs/TabContextContainer";
import {TabsContainer} from "../../components/common/reports/tabs/TabsContainer";
import {styled} from "@mui/material/styles";
import {BuyerNotificationSettings} from "./BuyerNotificationSettings";
import {SupplierNotificationSettings} from "./SupplierNotificationSettings";

const TabsBorderBottomGrey200 = styled(Tabs)(({theme}) => ({
  borderBottom: `${theme.palette.grey[200]} solid 0.07rem`,
  '& .MuiButtonBase-root.MuiTab-root ':{
    fontSize:'1em',
    // Keep each label on one line; below `md` the Tabs switch to `scrollable`,
    // so long labels ('Payout Bank Account') scroll instead of wrapping and
    // unbreakable ones ('Notifications') stop getting clipped by the scroller's
    // `overflow: hidden`.
    whiteSpace: 'nowrap'
  }
}));

const PATH_TO_TAB = {
  '/account/company-profile': 'company',
  '/account/payment-methods': 'payment',
  '/account/user-management': 'users',
  '/account/notification-settings': 'notifications',
};

// Inverse of PATH_TO_TAB, so clicking a tab keeps the URL in sync. Tabs without a
// dedicated route (e.g. 'subscriptions') fall back to plain local state.
const TAB_TO_PATH = Object.fromEntries(
  Object.entries(PATH_TO_TAB).map(([path, tab]) => [tab, path]),
);

const AccountOverview = ({ initialTab = 'company', account, handleAccountUpdated }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  // `md` is the knob: five uppercase labels need ~670px before `fullWidth`
  // starts squeezing them, which is inside the `md` range.
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const tabFromPath = PATH_TO_TAB[location.pathname] ?? initialTab;
  const [currentTab, setCurrentTab] = useState(tabFromPath);

  useEffect(() => {
    setCurrentTab(tabFromPath);
  }, [tabFromPath]);

  const handleTabChange = (_event, value) => {
    const path = TAB_TO_PATH[value];
    if (path && path !== location.pathname) {
      navigate(path);
    } else {
      setCurrentTab(value);
    }
  }
  return (
    <SecurSpaceContainer>
      <SecurSpaceReportContainer>
        <TabContextContainer>
          <TabContext value={currentTab + ''}>
            <TabsContainer>
              <TabsBorderBottomGrey200
                variant={isNarrow ? 'scrollable' : 'fullWidth'}
                scrollButtons={false}
                value={currentTab}
                onChange={handleTabChange}
                aria-label={'Account Tabs'}
                textColor={'secondary'}
              >
                <Tab value={'company'} label='Company'/>
                <Tab value={'payment'} label={isSupplierAccount(account) ? 'Payout Bank Account' : 'Payment Methods'}/>
                { hasAdminAccess(account) && isSupplierAccount(account) &&
                  <Tab value={'subscriptions'} label='Subscriptions'/>
                }
                <Tab value={'users'} label='Users'/>
                <Tab value={'notifications'} label='Notifications'/>
              </TabsBorderBottomGrey200>
              <TabPanel value={'company'}>
                <CompanyProfile account={account} handleAccountUpdated={handleAccountUpdated}/>
              </TabPanel>
              <TabPanel value={'payment'}>
                <PaymentMethods account={account}/>
              </TabPanel>
              <TabPanel value={'subscriptions'}>
                <SubscriptionManager account={account} handleAccountUpdated={handleAccountUpdated}/>
              </TabPanel>
              <TabPanel value={'users'}>
                <UserManagement account={account} />
              </TabPanel>
              <TabPanel value={'notifications'}>
                { isBuyerAccount(account) && <BuyerNotificationSettings /> }
                { isSupplierAccount(account) && <SupplierNotificationSettings /> }

              </TabPanel>
            </TabsContainer>
          </TabContext>
        </TabContextContainer>
      </SecurSpaceReportContainer>
    </SecurSpaceContainer>
  );
};

AccountOverview.propTypes = {
  initialTab: PropTypes.string,
  account: PropTypes.object.isRequired,
  handleAccountUpdated: PropTypes.func.isRequired,
};

export default AccountOverview;
